"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import {
  LuArrowLeft,
  LuCircleCheck,
  LuFileText,
  LuLoader,
  LuSend,
  LuUpload,
} from "react-icons/lu";
import { getAccessTokenAsync } from "@/store/useAuthStore";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

const OPPORTUNITY_TYPES = ["Internship", "Part-time", "Full-time", "Contractual"];
const INTEREST_AREAS = [
  "Marketing",
  "Content",
  "Research",
  "Community",
  "Product",
  "Design",
  "Operations",
  "Engineering",
  "Data & AI",
  "Other",
];

interface TalentForm {
  fullName: string;
  email: string;
  phone: string;
  opportunityTypes: string[];
  interestAreas: string[];
  about: string;
  whyLattu: string;
  intendedWork: string;
  linkedinUrl: string;
  portfolioUrl: string;
  cvUrl: string;
  consentGiven: boolean;
}

const EMPTY: TalentForm = {
  fullName: "",
  email: "",
  phone: "",
  opportunityTypes: [],
  interestAreas: [],
  about: "",
  whyLattu: "",
  intendedWork: "",
  linkedinUrl: "",
  portfolioUrl: "",
  cvUrl: "",
  consentGiven: false,
};

function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
}) {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-black dark:text-white">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                active
                  ? "bg-indigo-500 text-white border-indigo-500"
                  : "bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-zinc-600 dark:text-zinc-400 hover:border-indigo-300 dark:hover:border-indigo-500/40"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function IntroduceYourselfPage() {
  const [form, setForm] = useState<TalentForm>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({
        ...f,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleCvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = await getAccessTokenAsync();
    if (!token) {
      toast.error("Please sign in to upload a file, or paste a link instead.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadingCv(true);
    const toastId = toast.loading("Uploading CV/Resume…");
    try {
      const urlRes = await fetch(`${API}/careers/cv-upload-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
        }),
      });
      if (!urlRes.ok) {
        const body = await urlRes.json().catch(() => ({}));
        throw new Error(body?.message ?? "Failed to get upload URL.");
      }
      const { uploadUrl, fileUrl } = await urlRes.json();
      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("File upload to storage failed.");
      setForm((f) => ({ ...f, cvUrl: fileUrl }));
      toast.success("CV/Resume uploaded!", { id: toastId });
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Upload failed. Try pasting a link instead.";
      toast.error(msg, { id: toastId });
    } finally {
      setUploadingCv(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.opportunityTypes.length === 0) {
      toast.error("Please select at least one opportunity type.");
      return;
    }
    if (form.interestAreas.length === 0) {
      toast.error("Please select at least one area of interest.");
      return;
    }
    if (!form.consentGiven) {
      toast.error("Please check the consent box to proceed.");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Submitting your introduction…");
    try {
      const res = await fetch(`${API}/careers/talent-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = body?.message;
        throw new Error(
          Array.isArray(msg) ? msg.join(", ") : (msg ?? "Submission failed.")
        );
      }
      toast.success("Thanks! We've saved your introduction.", {
        id: toastId,
        duration: 4000,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
        { id: toastId, duration: 5000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-8">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
            <LuCircleCheck className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-black text-black dark:text-white">
            Thanks for introducing yourself.
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">
            We&apos;ve saved your details. When a role comes up that matches
            your interests and background, we&apos;ll reach out. This is a
            genuine commitment — not an automated response.
          </p>
          <Link
            href="/how-it-works"
            className="inline-flex items-center gap-2 bg-indigo-500 text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-600 hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(97,95,255,0.3)]"
          >
            See how Lattu AI works
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-transparent">
      {/* Back nav */}
      <div className="max-w-3xl mx-auto px-8 pt-10 pb-4">
        <Link
          href="/careers"
          className="inline-flex items-center gap-2 text-zinc-500 dark:text-zinc-400 hover:text-indigo-500 dark:hover:text-indigo-400 text-sm font-medium transition-colors"
        >
          <LuArrowLeft className="w-4 h-4" /> Back to careers
        </Link>
      </div>

      {/* Hero */}
      <section className="bg-[#ECEFF1] dark:bg-[#1a1a1a] py-16 transition-colors">
        <div className="max-w-3xl mx-auto px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-indigo-500 font-bold block">
            Stay on our radar
          </span>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-black dark:text-white">
            Introduce yourself
          </h1>
          <p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
            There may not be a role open right now that fits you. But we keep a
            genuine list of people we&apos;d like to work with as the team
            grows. If you believe in what Lattu AI is building — share who you
            are and what you can do. We read every submission.
          </p>
        </div>
      </section>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-8 py-16">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="space-y-4">
            <h2 className="text-xl font-black text-black dark:text-white border-b border-gray-200 dark:border-white/10 pb-3">
              Your details
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-black dark:text-white">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  name="fullName"
                  value={form.fullName}
                  onChange={handleField}
                  required
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-black dark:text-white">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleField}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-black dark:text-white">
                  Phone number{" "}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleField}
                  placeholder="+880..."
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Opportunity preferences */}
          <div className="space-y-6">
            <h2 className="text-xl font-black text-black dark:text-white border-b border-gray-200 dark:border-white/10 pb-3">
              What you&apos;re open to
            </h2>
            <MultiSelect
              label="Opportunity types (select all that apply) *"
              options={OPPORTUNITY_TYPES}
              selected={form.opportunityTypes}
              onChange={(v) => setForm((f) => ({ ...f, opportunityTypes: v }))}
            />
            <MultiSelect
              label="Areas of interest (select all that apply) *"
              options={INTEREST_AREAS}
              selected={form.interestAreas}
              onChange={(v) => setForm((f) => ({ ...f, interestAreas: v }))}
            />
          </div>

          {/* About you */}
          <div className="space-y-6">
            <h2 className="text-xl font-black text-black dark:text-white border-b border-gray-200 dark:border-white/10 pb-3">
              Tell us about yourself
            </h2>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-black dark:text-white">
                About you — background, experience, skills{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="about"
                value={form.about}
                onChange={handleField}
                required
                rows={5}
                placeholder="Who are you, what have you done, what are you good at?"
                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors resize-y"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-black dark:text-white">
                Why Lattu AI?{" "}
                <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <textarea
                name="whyLattu"
                value={form.whyLattu}
                onChange={handleField}
                rows={4}
                placeholder="What is it about Lattu AI that interests you? What do you want to contribute to?"
                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors resize-y"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-black dark:text-white">
                What kind of work you want to do{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="intendedWork"
                value={form.intendedWork}
                onChange={handleField}
                required
                rows={4}
                placeholder="Be specific — what tasks, projects, or problems do you want to work on?"
                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors resize-y"
              />
            </div>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h2 className="text-xl font-black text-black dark:text-white border-b border-gray-200 dark:border-white/10 pb-3">
              Links &amp; materials
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-black dark:text-white">
                  LinkedIn URL <span className="text-red-500">*</span>
                </label>
                <input
                  name="linkedinUrl"
                  value={form.linkedinUrl}
                  onChange={handleField}
                  required
                  placeholder="https://linkedin.com/in/..."
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-black dark:text-white">
                  Portfolio / website{" "}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </label>
                <input
                  name="portfolioUrl"
                  value={form.portfolioUrl}
                  onChange={handleField}
                  placeholder="https://yourportfolio.com"
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-black dark:text-white">
                CV / Resume{" "}
                <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleCvUpload}
              />
              <div className="flex gap-3 items-center flex-wrap">
                <input
                  name="cvUrl"
                  value={form.cvUrl}
                  onChange={handleField}
                  placeholder="Paste a Drive / Dropbox link, or upload a file"
                  className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-black dark:text-white placeholder-zinc-400 focus:outline-none focus:border-indigo-400 dark:focus:border-indigo-500 transition-colors"
                />
                <button
                  type="button"
                  disabled={uploadingCv}
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0 inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:border-indigo-300 dark:hover:border-indigo-500/40 transition-colors disabled:opacity-60"
                >
                  {uploadingCv ? (
                    <LuLoader className="w-4 h-4 animate-spin" />
                  ) : form.cvUrl ? (
                    <LuFileText className="w-4 h-4 text-green-500" />
                  ) : (
                    <LuUpload className="w-4 h-4" />
                  )}
                  {uploadingCv ? "Uploading…" : "Upload file"}
                </button>
              </div>
              {form.cvUrl && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  CV/Resume attached ✓
                </p>
              )}
            </div>
          </div>

          {/* Consent + Submit */}
          <div className="space-y-6 pt-4 border-t border-gray-200 dark:border-white/10">
            <div className="flex items-start gap-3">
              <input
                id="consentGiven"
                name="consentGiven"
                type="checkbox"
                checked={form.consentGiven}
                onChange={handleField}
                className="mt-1 w-4 h-4 accent-indigo-500"
              />
              <label
                htmlFor="consentGiven"
                className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed"
              >
                I confirm the information I&apos;ve shared is accurate. I
                consent to Lattu AI keeping my details on file and contacting me
                about future opportunities. I understand this is not a job offer
                or guarantee of employment.
              </label>
            </div>
            <button
              type="submit"
              disabled={submitting || !form.consentGiven}
              className="inline-flex items-center gap-2 bg-indigo-500 text-white px-8 py-4 rounded-2xl font-bold hover:bg-indigo-600 hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(97,95,255,0.3)] disabled:opacity-60 disabled:pointer-events-none"
            >
              {submitting ? (
                <LuLoader className="w-5 h-5 animate-spin" />
              ) : (
                <LuSend className="w-5 h-5" />
              )}
              {submitting ? "Submitting…" : "Submit introduction"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
